import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  listGeometriesInfiniteOptions,
  useDashboardGeometries,
} from '../../../hooks/useDashboardGeometries';
import { useRenameGeometry } from '../../../hooks/useRenameGeometry';
import DashboardPanel from './-components/DashboardPanel';

function DashboardPage() {
  const navigate = useNavigate();
  const dashboard = useDashboardGeometries();
  const renameGeometry = useRenameGeometry();
  // `?prep=wrap` pre-selects Wrap in the per-file UploadPrepDialog (via
  // DashboardPanel's initialGeoPrep) — set by the "re-import with Wrap" prompt /
  // needs_wrap CTA so a re-import lands defaulted to Wrap.
  const { prep } = Route.useSearch();

  const handleOpenGeometry = (projectId: string) => {
    void navigate({ to: '/meshgen/$uuid', params: { uuid: projectId } });
  };

  return (
    <div className="h-full flex-1 overflow-hidden">
      <DashboardPanel
        initialGeoPrep={prep === 'wrap' ? 'ftm' : 'repair'}
        geometries={dashboard.geometries}
        isLoading={dashboard.isLoading}
        error={dashboard.error}
        onOpenGeometry={handleOpenGeometry}
        onDeleteGeometry={(projectId) => {
          void dashboard.deleteGeometry(projectId);
        }}
        onDownloadGeometry={(projectId, fileName, downloadType) => {
          void dashboard.downloadGeometry(projectId, fileName, downloadType);
        }}
        onEditFileName={(projectId, value) => {
          const normalized = value.trim();
          if (normalized) {
            renameGeometry.mutate({
              geometryId: projectId,
              filename: normalized,
            });
          }
        }}
        hasNextPage={dashboard.hasNextPage}
        isFetchingNextPage={dashboard.isFetchingNextPage}
        onLoadMore={() => void dashboard.fetchNextPage()}
      />
    </div>
  );
}

export const Route = createFileRoute('/_app/meshgen/')({
  // `?prep=wrap` pre-selects Wrap in the per-file UploadPrepDialog (from the
  // re-import prompt). Only 'wrap' is meaningful; anything else drops to
  // undefined so the dialog keeps its 'repair' default and the URL stays clean.
  validateSearch: (search: Record<string, unknown>): { prep?: 'wrap' } => ({
    prep: search.prep === 'wrap' ? 'wrap' : undefined,
  }),
  // Warm the first page of geometries so the dashboard lands on content
  // instead of a spinner. Best-effort: on failure the component's
  // useInfiniteQuery owns retries and error UI.
  loader: async ({ context: { queryClient } }) => {
    await queryClient
      .ensureInfiniteQueryData(listGeometriesInfiniteOptions())
      .catch(() => undefined);
  },
  component: DashboardPage,
});
